import React, { useEffect, useState } from 'react';
import { Award, Users, Heart, Shield, Target, Eye, Building, Star, Trophy, CheckCircle, TrendingUp } from 'lucide-react';

const AboutUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [countersAnimated, setCountersAnimated] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
    const handleScroll = () => {
      const achievementSection = document.getElementById('achievements');
      if (achievementSection) {
        const rect = achievementSection.getBoundingClientRect();
        if (rect.top < window.innerHeight && !countersAnimated) {
          setCountersAnimated(true);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [countersAnimated]);

  const certifications = [
    { name: 'ISO 9001:2015', logo: Trophy, description: 'Quality Management System' },
    { name: 'NABH Accredited', logo: Star, description: 'National Accreditation Board' },
    { name: 'NABL Certified', logo: CheckCircle, description: 'Laboratory Accreditation' },
    { name: 'CGHS Empaneled', logo: Building, description: 'Government Health Scheme' },
  ];

  const achievements = [
    { number: '15+', label: 'Years of Excellence', icon: Award },
    { number: '10,000+', label: 'Successful Surgeries', icon: TrendingUp },
    { number: '25,000+', label: 'Happy Patients', icon: Users },
    { number: '98%', label: 'Success Rate', icon: Star },
  ];

  const values = [
    {
      icon: Heart,
      title: 'Compassionate Care',
      description: 'We treat every patient with empathy, understanding their pain and concerns.',
      bgColor: 'bg-red-50',
      iconColor: 'text-red-600',
      borderColor: 'border-red-200'
    },
    {
      icon: Shield,
      title: 'Excellence in Treatment',
      description: 'Using the latest technology and techniques to ensure the best outcomes.',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-200'
    },
    {
      icon: Users,
      title: 'Patient-Centered Approach',
      description: 'Every treatment plan is personalized to meet individual patient needs.',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      borderColor: 'border-green-200'
    },
    {
      icon: Award,
      title: 'Continuous Innovation',
      description: 'Staying updated with the latest advances in orthopedic medicine.',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      borderColor: 'border-purple-200'
    },
  ];

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white py-16 md:py-20 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/2 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>
          <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Content - Vertically Centered */}
          <div className="text-center mb-12 md:mb-16">
            <div className={`transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <span className="inline-block px-5 py-2 bg-blue-500/20 backdrop-blur-md text-blue-200 rounded-full text-sm font-semibold mb-4 border border-blue-400/30">
                Excellence in Orthopedic Care Since 2010
              </span>
            </div>
            
            <h1 className={`text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              About Sharma
            </h1>
            
            <div className="relative inline-block mb-8">
              <div className="h-1 w-64 md:w-96 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 rounded-full mx-auto"></div>
            </div>
            
            <p className={`text-lg md:text-xl lg:text-2xl text-blue-100 max-w-4xl mx-auto leading-relaxed font-light mb-8 transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              Where <span className="font-semibold text-white">world-class orthopedic care</span> meets compassion, innovation, and an unwavering commitment to <span className="font-semibold text-white">your wellness journey</span>.
            </p>
            
            {/* Trust badges */}
            <div className={`flex flex-wrap justify-center gap-3 md:gap-4 transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-md px-4 py-2 md:px-5 md:py-3 rounded-full border border-white/20">
                <Star className="h-4 w-4 md:h-5 md:w-5 text-yellow-400 fill-yellow-400" />
                <span className="text-xs md:text-sm font-medium">25,000+ Happy Patients</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-md px-4 py-2 md:px-5 md:py-3 rounded-full border border-white/20">
                <Award className="h-4 w-4 md:h-5 md:w-5 text-green-400" />
                <span className="text-xs md:text-sm font-medium">98% Success Rate</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-md px-4 py-2 md:px-5 md:py-3 rounded-full border border-white/20">
                <Trophy className="h-4 w-4 md:h-5 md:w-5 text-blue-400" />
                <span className="text-xs md:text-sm font-medium">15+ Years Excellence</span>
              </div>
          </div>
        </div>

        </div>
      </section>

      {/* Our Story Section - Large Text Narrative */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-purple-100/30 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Section Title */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
              Our Journey
            </span>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-8 leading-tight">
            Our Story
          </h2>
            <div className="w-32 h-1.5 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 mx-auto rounded-full"></div>
          </div>
          
          {/* Story Content - Large Typography */}
          <div className="space-y-12">
            <div className="group">
              <p className="text-2xl md:text-3xl lg:text-4xl text-gray-800 leading-relaxed font-light text-center mb-8 hover:text-gray-900 transition-colors duration-300">
                <span className="font-serif italic text-blue-700">It all began in 2010</span> with a 
                <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> simple yet powerful vision</span> — 
                to create a place where cutting-edge orthopedic care meets 
                <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600"> genuine compassion</span>.
              </p>
            </div>
            
            <div className="flex justify-center my-12">
              <div className="w-2 h-2 bg-blue-400 rounded-full mx-2"></div>
              <div className="w-2 h-2 bg-purple-400 rounded-full mx-2"></div>
              <div className="w-2 h-2 bg-pink-400 rounded-full mx-2"></div>
            </div>
            
            <div className="group">
              <p className="text-2xl md:text-3xl lg:text-4xl text-gray-800 leading-relaxed font-light text-center mb-8 hover:text-gray-900 transition-colors duration-300">
                Founded by <span className="font-semibold text-blue-700">Dr. Rajesh Sharma</span>, 
                our center started as a modest clinic but carried 
                <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-600"> extraordinary ambitions</span>. 
                We believed every patient deserved not just treatment, but 
                <span className="font-serif italic text-purple-700"> a partner in their healing journey</span>.
              </p>
            </div>
            
            <div className="flex justify-center my-12">
              <div className="w-2 h-2 bg-green-400 rounded-full mx-2"></div>
              <div className="w-2 h-2 bg-teal-400 rounded-full mx-2"></div>
              <div className="w-2 h-2 bg-blue-400 rounded-full mx-2"></div>
            </div>
            
            <div className="group">
              <p className="text-2xl md:text-3xl lg:text-4xl text-gray-800 leading-relaxed font-light text-center mb-8 hover:text-gray-900 transition-colors duration-300">
                Over <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">15 remarkable years</span>, 
                we've had the privilege of transforming 
                <span className="font-semibold text-green-700">25,000+ lives</span> — 
                from professional athletes chasing their dreams to grandparents wanting to play with their grandchildren. 
                <span className="font-serif italic text-indigo-700">Each story fuels our passion</span>.
              </p>
            </div>
            
            <div className="flex justify-center my-12">
              <div className="w-2 h-2 bg-orange-400 rounded-full mx-2"></div>
              <div className="w-2 h-2 bg-red-400 rounded-full mx-2"></div>
              <div className="w-2 h-2 bg-pink-400 rounded-full mx-2"></div>
            </div>
            
            <div className="group">
              <p className="text-2xl md:text-3xl lg:text-4xl text-gray-800 leading-relaxed font-light text-center mb-8 hover:text-gray-900 transition-colors duration-300">
                Today, we stand as 
                <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"> more than just a medical center</span>. 
                We are a 
                <span className="font-semibold text-blue-700">community of healers</span>, 
                innovators, and compassionate caregivers, united by one mission: 
                <span className="font-serif italic text-lg md:text-2xl lg:text-3xl block mt-6 text-purple-700">
                  "To restore not just mobility, but hope, dignity, and the joy of living pain-free."
                </span>
              </p>
            </div>
            
            {/* Closing Statement */}
            <div className="mt-16 text-center">
              <div className="inline-block bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-0.5 rounded-2xl">
                <div className="bg-white px-8 py-6 rounded-2xl">
                  <p className="text-xl md:text-2xl font-medium text-gray-800">
                    This is our story. 
                    <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 font-bold">
                      We'd be honored to be part of yours.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-white via-gray-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
              Our Direction
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Mission & Vision
            </h2>
            <div className="w-32 h-1.5 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Mission */}
            <div className="relative group h-full">
              <div className="absolute -inset-1 bg-gradient-to-br from-blue-500 via-blue-600 to-cyan-500 rounded-3xl blur-lg opacity-30 group-hover:opacity-60 transition duration-700"></div>
              <div className="relative h-full bg-white p-10 md:p-12 rounded-3xl shadow-2xl border border-blue-100 flex flex-col">
                <div className="flex items-center mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl blur-md opacity-50"></div>
                    <div className="relative bg-gradient-to-br from-blue-500 to-cyan-600 p-4 rounded-2xl shadow-xl">
                      <Target className="h-10 w-10 text-white" />
                    </div>
                  </div>
                  <div className="ml-5">
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900">Our Mission</h3>
                    <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mt-2 rounded-full"></div>
                  </div>
              </div>
                
                <p className="text-gray-700 leading-relaxed text-lg md:text-xl mb-8 flex-grow">
                  To provide <span className="font-semibold text-blue-700">comprehensive, compassionate, and innovative</span> orthopedic care
                that restores function, alleviates pain, and improves the quality of life
                  for our patients. We strive to be the <span className="font-semibold text-cyan-700">leading orthopedic center</span> by
                combining advanced medical technology with personalized patient care.
              </p>
                
                <div className="space-y-3">
                  <div className="flex items-center text-blue-600 font-medium">
                    <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Patient-Centered Approach</span>
                  </div>
                  <div className="flex items-center text-blue-600 font-medium">
                    <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Advanced Medical Technology</span>
                  </div>
                  <div className="flex items-center text-blue-600 font-medium">
                    <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Excellence Since 2010</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Vision */}
            <div className="relative group h-full">
              <div className="absolute -inset-1 bg-gradient-to-br from-purple-500 via-purple-600 to-pink-500 rounded-3xl blur-lg opacity-30 group-hover:opacity-60 transition duration-700"></div>
              <div className="relative h-full bg-white p-10 md:p-12 rounded-3xl shadow-2xl border border-purple-100 flex flex-col">
                <div className="flex items-center mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl blur-md opacity-50"></div>
                    <div className="relative bg-gradient-to-br from-purple-500 to-pink-600 p-4 rounded-2xl shadow-xl">
                      <Eye className="h-10 w-10 text-white" />
                    </div>
                  </div>
                  <div className="ml-5">
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900">Our Vision</h3>
                    <div className="w-16 h-1 bg-gradient-to-r from-purple-500 to-pink-500 mt-2 rounded-full"></div>
                  </div>
              </div>
                
                <p className="text-gray-700 leading-relaxed text-lg md:text-xl mb-8 flex-grow">
                  To be recognized as the <span className="font-semibold text-purple-700">premier orthopedic healthcare destination</span>,
                setting new standards in patient care, medical innovation, and clinical
                excellence. We envision a future where every patient receives
                  <span className="font-semibold text-pink-700"> world-class treatment</span> in a compassionate and healing environment.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center text-purple-600 font-medium">
                    <Star className="h-5 w-5 mr-3 flex-shrink-0 fill-purple-600" />
                    <span>Setting Healthcare Standards</span>
                  </div>
                  <div className="flex items-center text-purple-600 font-medium">
                    <Star className="h-5 w-5 mr-3 flex-shrink-0 fill-purple-600" />
                    <span>Continuous Innovation</span>
                  </div>
                  <div className="flex items-center text-purple-600 font-medium">
                    <Star className="h-5 w-5 mr-3 flex-shrink-0 fill-purple-600" />
                    <span>Compassionate Healing Environment</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-300/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-purple-300/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-6">
              What Drives Us
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Our Core Values
            </h2>
            <div className="w-32 h-1.5 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 mx-auto rounded-full mb-6"></div>
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto">
              The principles that guide everything we do at Sharma Ortho Center.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="relative h-full">
                  <div className={`absolute -inset-0.5 bg-gradient-to-br ${value.iconColor === 'text-red-600' ? 'from-red-500 to-pink-500' : value.iconColor === 'text-blue-600' ? 'from-blue-500 to-cyan-500' : value.iconColor === 'text-green-600' ? 'from-green-500 to-teal-500' : 'from-purple-500 to-pink-500'} rounded-2xl blur opacity-30 group-hover:opacity-70 transition duration-500`}></div>
                  <div className="relative bg-white p-8 rounded-2xl shadow-xl h-full flex flex-col hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2">
                    <div className="flex justify-center mb-6">
                      <div className={`relative`}>
                        <div className={`absolute inset-0 ${value.bgColor} rounded-2xl blur-md opacity-50`}></div>
                        <div className={`relative ${value.bgColor} w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-transform duration-500`}>
                          <value.icon className={`h-10 w-10 ${value.iconColor}`} />
                        </div>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-center flex-grow">{value.description}</p>
                    <div className={`mt-6 h-1 w-0 bg-gradient-to-r ${value.iconColor === 'text-red-600' ? 'from-red-500 to-pink-500' : value.iconColor === 'text-blue-600' ? 'from-blue-500 to-cyan-500' : value.iconColor === 'text-green-600' ? 'from-green-500 to-teal-500' : 'from-purple-500 to-pink-500'} mx-auto rounded-full group-hover:w-full transition-all duration-700`}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements - Simplified */}
      <section id="achievements" className="py-20 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
              Our Impact
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Achievements
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Numbers that reflect our commitment to excellence in orthopedic care.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-8 text-center shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-xl mb-6">
                  <achievement.icon className="w-8 h-8 text-blue-600" />
                </div>
                <div className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
                  {achievement.number}
                </div>
                <div className="text-gray-600 font-medium text-lg">
                  {achievement.label}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 text-lg italic">
              "Every number represents a life changed, a pain relieved, a dream restored."
            </p>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-6">
              Quality Assurance
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Certifications & Accreditations
            </h2>
            <div className="w-32 h-1.5 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 mx-auto rounded-full mb-6"></div>
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto">
              Recognized for our unwavering commitment to quality healthcare standards and excellence in patient care.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {certifications.map((cert, index) => (
              <div key={index} className="group">
                <div className="relative h-full">
                  <div className="absolute -inset-1 bg-gradient-to-br from-blue-400 via-purple-400 to-pink-400 rounded-3xl blur opacity-20 group-hover:opacity-50 transition duration-500"></div>
                  <div className="relative bg-white p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-3 border-2 border-gray-100 group-hover:border-blue-200 h-full flex flex-col">
                    <div className="flex justify-center mb-6">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl blur-md opacity-30"></div>
                        <div className="relative bg-gradient-to-br from-blue-50 to-purple-50 w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-transform duration-500">
                          <cert.logo className="h-10 w-10 text-blue-600" />
                        </div>
                      </div>
                    </div>
                    <h3 className="font-bold text-xl text-gray-900 text-center mb-3 group-hover:text-blue-700 transition-colors">{cert.name}</h3>
                    <p className="text-gray-500 text-center flex-grow">{cert.description}</p>
                    <div className="mt-6 flex justify-center">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                    <div className="mt-4 h-1 w-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 mx-auto rounded-full group-hover:w-full transition-all duration-700"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1.5s'}}></div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '3s'}}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-2 bg-blue-500/20 backdrop-blur-md text-blue-200 rounded-full text-sm font-semibold mb-6 border border-blue-400/30">
              The Sharma Difference
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Why Patients Choose Us
            </h2>
            <div className="w-32 h-1.5 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 mx-auto rounded-full mb-6"></div>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto">
              Experience the difference that expert care and genuine compassion can make in your healing journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Card 1 */}
            <div className="group relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-blue-400 via-cyan-400 to-teal-400 rounded-3xl blur-lg opacity-40 group-hover:opacity-70 transition duration-700"></div>
              <div className="relative bg-white/10 backdrop-blur-md p-10 rounded-3xl shadow-2xl border-2 border-white/20 hover:border-white/40 transition-all duration-500 group-hover:-translate-y-3 h-full flex flex-col">
                <div className="flex justify-center mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-2xl blur-lg opacity-50"></div>
                    <div className="relative bg-gradient-to-br from-blue-500 to-cyan-500 w-20 h-20 rounded-2xl flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:rotate-6 transition-transform duration-500">
                      <Building className="h-10 w-10 text-white" />
                    </div>
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">State-of-the-Art Facility</h3>
                <p className="text-blue-100 text-center leading-relaxed text-lg flex-grow mb-6">Modern infrastructure with advanced medical equipment and comfortable patient rooms designed for optimal healing.</p>
                <div className="space-y-3">
                  <div className="flex items-center text-cyan-300 font-medium">
                    <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Latest Technology</span>
                  </div>
                  <div className="flex items-center text-cyan-300 font-medium">
                    <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Modern Infrastructure</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Card 2 */}
            <div className="group relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-purple-400 via-pink-400 to-rose-400 rounded-3xl blur-lg opacity-40 group-hover:opacity-70 transition duration-700"></div>
              <div className="relative bg-white/10 backdrop-blur-md p-10 rounded-3xl shadow-2xl border-2 border-white/20 hover:border-white/40 transition-all duration-500 group-hover:-translate-y-3 h-full flex flex-col">
                <div className="flex justify-center mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-pink-400 rounded-2xl blur-lg opacity-50"></div>
                    <div className="relative bg-gradient-to-br from-purple-500 to-pink-500 w-20 h-20 rounded-2xl flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:rotate-6 transition-transform duration-500">
                      <Users className="h-10 w-10 text-white" />
                    </div>
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">Expert Medical Team</h3>
                <p className="text-blue-100 text-center leading-relaxed text-lg flex-grow mb-6">Board-certified orthopedic surgeons with specialized training and years of experience in complex procedures.</p>
                <div className="space-y-3">
                  <div className="flex items-center text-pink-300 font-medium">
                    <Award className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Board Certified</span>
                  </div>
                  <div className="flex items-center text-pink-300 font-medium">
                    <Award className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>15+ Years Experience</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Card 3 */}
            <div className="group relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-green-400 via-emerald-400 to-teal-400 rounded-3xl blur-lg opacity-40 group-hover:opacity-70 transition duration-700"></div>
              <div className="relative bg-white/10 backdrop-blur-md p-10 rounded-3xl shadow-2xl border-2 border-white/20 hover:border-white/40 transition-all duration-500 group-hover:-translate-y-3 h-full flex flex-col">
                <div className="flex justify-center mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-400 rounded-2xl blur-lg opacity-50"></div>
                    <div className="relative bg-gradient-to-br from-green-500 to-emerald-500 w-20 h-20 rounded-2xl flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:rotate-6 transition-transform duration-500">
                      <Heart className="h-10 w-10 text-white" />
                    </div>
                  </div>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">Personalized Care</h3>
                <p className="text-blue-100 text-center leading-relaxed text-lg flex-grow mb-6">Individual attention with customized treatment plans designed for optimal recovery outcomes and patient satisfaction.</p>
                <div className="space-y-3">
                  <div className="flex items-center text-emerald-300 font-medium">
                    <Heart className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Patient First Approach</span>
                  </div>
                  <div className="flex items-center text-emerald-300 font-medium">
                    <Heart className="h-5 w-5 mr-3 flex-shrink-0" />
                    <span>Customized Treatment</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
